/**
 * Fit Into Box
 *
 * Places the whole motif, centered, inside a sub-rectangle of the target
 * area and translates the result back into area-relative coordinates. The
 * carry-over rules 2, 3a and 3b describe their result as such a box (X % of
 * the area, hung from the top or centered); the KLEIN rule and RUNDUM are
 * the same composition with a different box and seed.
 *
 * There is no fit arithmetic here: the contain-fit is `fitInside`, called
 * on the box as if it were the area. A second fit formula is what once made
 * the product card and the editor disagree.
 */

import type { RelativeDesignState } from './placementTypes.js';
import { fitInside } from './fit.js';
import type { CarryOverAnchor } from './carryOverDefaults.js';
import type { CarryOverBox } from './carryOverRule.js';
import type { PlacementArea } from '../placement/sizeTier.js';

/** A sub-rectangle of the target area in the area's pixels. */
export interface BoxRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type AnchorFallback = 'no-mm' | 'offset-exceeds-area';

export interface FitIntoBoxResult {
  placement: RelativeDesignState;
  /** The anchor that was applied after any fallback. */
  anchor: CarryOverAnchor;
  /** Set when anchor TOP could not be honoured and the box was centered. */
  fallback?: AnchorFallback;
}

function usableMm(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

/**
 * The whole motif, centered, as wide as `seedRelWidth` of the box allows,
 * fitted inside the box and mapped back onto the area. With `topAligned`
 * the fitted motif hangs from the box's top edge instead of its center.
 */
export function fitIntoRect(
  rel: RelativeDesignState,
  target: PlacementArea,
  rect: BoxRect,
  seedRelWidth = 1,
  topAligned = false
): RelativeDesignState {
  const box: PlacementArea = { width: rect.width, height: rect.height };
  const fitted = fitInside(
    {
      relCenterX: 0.5,
      relCenterY: 0.5,
      relWidth: Math.min(1, Math.max(seedRelWidth, 0)) || 1,
      designRatio: rel.designRatio,
    },
    box
  );
  const fittedRelHeight =
    (fitted.relWidth * fitted.designRatio * box.width) / box.height;
  const centerYInBox = topAligned ? fittedRelHeight / 2 : fitted.relCenterY;

  // A box that IS the area maps one to one; skip the arithmetic so the
  // result equals `fitInside` bit for bit (no second fit formula).
  const isWholeArea =
    rect.left === 0 &&
    rect.top === 0 &&
    rect.width === target.width &&
    rect.height === target.height;
  if (isWholeArea) {
    return topAligned ? { ...fitted, relCenterY: centerYInBox } : fitted;
  }

  return {
    relCenterX: (rect.left + fitted.relCenterX * rect.width) / target.width,
    relCenterY: (rect.top + centerYInBox * rect.height) / target.height,
    relWidth: (fitted.relWidth * rect.width) / target.width,
    designRatio: rel.designRatio,
  };
}

export interface CarryOverBoxRectResult {
  rect: BoxRect;
  /** The anchor that was applied after any fallback. */
  anchor: CarryOverAnchor;
  /** Set when anchor TOP could not be honoured and the box was centered. */
  fallback?: AnchorFallback;
}

function pxPerMmOf(target: PlacementArea): number | null {
  const targetMm = usableMm(target.maxWidthMm);
  return targetMm === null ? null : target.width / targetMm;
}

/**
 * The sub-rectangle a percent box takes in the target area, in the area's
 * pixels: `percent` of the area on both axes, centered horizontally;
 * vertically centered, or hung `topOffsetMm` below the top edge with its
 * height limited to what remains. Without millimetres on the target the
 * offset cannot be converted and the box is centered instead. The admin
 * preview draws this same rect, so it cannot drift from the carry-over.
 */
export function carryOverBoxRect(
  target: PlacementArea,
  box: Pick<CarryOverBox, 'percent' | 'anchor' | 'topOffsetMm'>
): CarryOverBoxRectResult {
  const share = box.percent / 100;
  const pxPerMm = pxPerMmOf(target);

  let anchor: CarryOverAnchor = box.anchor;
  let fallback: AnchorFallback | undefined;
  let height = share * target.height;
  let top = (target.height - height) / 2;

  if (anchor === 'TOP') {
    if (pxPerMm === null) {
      anchor = 'CENTER';
      fallback = 'no-mm';
    } else {
      const offsetPx = Math.max(0, box.topOffsetMm) * pxPerMm;
      const available = target.height - offsetPx;
      if (available <= 0) {
        anchor = 'CENTER';
        fallback = 'offset-exceeds-area';
      } else {
        height = Math.min(height, available);
        top = offsetPx;
      }
    }
  }

  const width = share * target.width;
  const rect: BoxRect = {
    left: (target.width - width) / 2,
    top,
    width,
    height,
  };
  return fallback ? { rect, anchor, fallback } : { rect, anchor };
}

/**
 * Rule 2 / 3a / 3b: the box is `percent` of the area on both axes, centered
 * horizontally; vertically centered, or hung `topOffsetMm` below the top
 * edge with its height limited to what remains. Without millimetres on the
 * target the offset cannot be converted and the box is centered instead.
 * With `capAtMasterMm` the seed is never wider in mm than on the master
 * (needs mm on both sides; without them there is no cap).
 */
export function fitIntoBox(
  rel: RelativeDesignState,
  source: PlacementArea,
  target: PlacementArea,
  box: CarryOverBox
): FitIntoBoxResult {
  if (!(box.percent >= 1 && box.percent <= 100)) {
    throw new RangeError(
      `carry-over percent must be between 1 and 100, got ${box.percent}`
    );
  }
  const { rect, anchor, fallback } = carryOverBoxRect(target, box);
  const pxPerMm = pxPerMmOf(target);

  let seedRelWidth = 1;
  const sourceMm = usableMm(source.maxWidthMm);
  if (box.capAtMasterMm && sourceMm !== null && pxPerMm !== null) {
    const masterWidthMm = rel.relWidth * sourceMm;
    const boxWidthMm = rect.width / pxPerMm;
    seedRelWidth = Math.min(1, masterWidthMm / boxWidthMm);
  }

  const placement = fitIntoRect(
    rel,
    target,
    rect,
    seedRelWidth,
    anchor === 'TOP'
  );
  return fallback ? { placement, anchor, fallback } : { placement, anchor };
}
